import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import { brandAuthService } from "@/services/brand-auth.service";
import { ArenaLogo } from "@/components/common/ArenaLogo";
import { clearOnboardingSkip } from "@/lib/brandOnboarding";

export function InstallApp() {
  const [isValidating, setIsValidating] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // logout from all previous sessions
  useEffect(() => {
    localStorage.removeItem("brand_user");
    localStorage.removeItem("brand_token");
    localStorage.removeItem("brand_refresh_token");
    // Fresh session: incomplete setups should land on onboarding again
    clearOnboardingSkip();
  }, []);

  useEffect(() => {
    const validateToken = async (token: string) => {
      setIsValidating(true);
      try {
        const response = await brandAuthService.getShopifyDetail(token);
        if (response?.shop) {
          // Token is valid, store it and redirect to signup with shop details
          localStorage.setItem("brand_temp_token", token);

          // Handle onboarding status
          if (response.onboardingStatus) {
            const { status } = response.onboardingStatus;

            if (status === "COMPLETED") {
              // If onboarding is completed, set the token and trigger auth context update
              localStorage.setItem("brand_token", token);
              // Force a storage event since it doesn't fire for same-tab changes
              window.dispatchEvent(new Event("storage"));
              // Small delay to allow auth context to update
              setTimeout(() => {
                // Check if there's a redirect path (e.g., from no subscription)
                if (redirectPath) {
                  navigate(redirectPath, { replace: true });
                } else {
                  navigate("/brand/dashboard", { replace: true });
                }
              }, 100);
              return;
            }

            // For PERSONAL_DETAIL or COMPANY_DETAIL, navigate to signup with the appropriate step
            navigate("/brand/auth/signup", {
              replace: true,
              state: {
                email: response.shop.email,
                firstName:
                  response.shop.name.split(" ")[0] || response.shop.name,
                lastName: response.shop.name.split(" ")[1] || "",
                country: response.shop.billingAddress.country,
                shopifyDomain: response.shop.myshopifyDomain,
                companyName: response.shop.name,
                initialStep:
                  status === "PERSONAL_DETAILS" ? "personal" : "company",
                onboardingData: response.onboardingStatus.data,
              },
            });
          } else {
            // No onboarding status, start from beginning
            navigate("/brand/auth/signup", {
              replace: true,
              state: {
                email: response.shop.email,
                firstName:
                  response.shop.name.split(" ")[0] || response.shop.name,
                lastName: response.shop.name.split(" ")[1] || "",
                country: response.shop.billingAddress.country,
                shopifyDomain: response.shop.myshopifyDomain,
                companyName: response.shop.name,
                initialStep: "personal",
              },
            });
          }
        } else {
          // Invalid token
          toast.error(
            "Invalid or expired token. Please install the app again."
          );
          localStorage.removeItem("brand_temp_token");
        }
      } catch (error) {
        console.error("Token validation error:", error);
        toast.error("Failed to validate token. Please try again.");
        localStorage.removeItem("brand_temp_token");
      } finally {
        setIsValidating(false);
      }
    };

    // Check for token in URL
    const params = new URLSearchParams(location.search);
    const token = params.get("token");
    const redirectPath = params.get("redirect");
    const clearToken = params.get("clear_token");

    // If clear_token is true, remove brand_user and brand_token from localStorage
    if (clearToken === "true") {
      // localStorage.removeItem("brand_user");
      // localStorage.removeItem("brand_token");
      // Force a storage event to notify auth context
      window.dispatchEvent(new Event("storage"));
    }

    if (token) {
      validateToken(token);
    }
  }, [location, navigate]);

  if (isValidating) {
    return (
      <div className="mx-auto flex items-center justify-center h-screen">
        <div className="md:w-[440px] flex items-center justify-center px-5 py-7 bg-card border border-border rounded-[24px] shadow-[1px_5px_10px_0px_#D1D1D11A,4px_18px_19px_0px_#D1D1D117,9px_41px_25px_0px_#D1D1D10D,15px_73px_30px_0px_#D1D1D103,24px_115px_33px_0px_#D1D1D100]  ">
          <div className="w-full max-w-[440px]">
            <div className="flex flex-col items-center justify-center gap-6 mb-4">
              <div className="w-16 h-16 border-4 border-brand-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-muted-foreground">"Validating your store..." </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex items-center justify-center ">
      <div className="w-full flex items-center justify-center px-5 py-7 bg-card border border-border rounded-[24px] shadow-[1px_5px_10px_0px_#D1D1D11A,4px_18px_19px_0px_#D1D1D117,9px_41px_25px_0px_#D1D1D10D,15px_73px_30px_0px_#D1D1D103,24px_115px_33px_0px_#D1D1D100]  ">
        <div className="w-full max-w-[440px]">
          <div className="flex flex-col items-center justify-center gap-6 mb-4">
            <ArenaLogo className="w-12 border border-black mx-auto mb- rounded-[12px] bg-[#f6f6f6]" />
            <h1 className="font-bold text-[24px] text-foreground/60 leading-[24px] tracking-[0.16px] text-center align-middle">
              Install Reveshare on Shopify
            </h1>
            <p className="font-normal text-[14px] w-[330px] tracking-normal text-center align-middle text-muted-foreground">
              To get started with Reveshare, install our app from the Shopify
              App Store
            </p>
          </div>

          <a
            href={
              import.meta.env.VITE_SHOPIFY_APP_STORE_URL ||
              "https://apps.shopify.com"
            }
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex justify-center items-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
          >
            Install from Shopify App Store
          </a>

          <div className="mt-4 text-center border border-brand-600 shadow-[0px_1px_2px_0px_#0A0D120D] rounded-lg p-2">
            <span className="font-bold text-[14px] leading-[20px] tracking-normal text-muted-foreground">
              Already have an account?{" "}
            </span>
            <Link
              to="/auth/login"
              className="text-sm text-brand-600 font-bold text-[14px] tracking-normal hover:text-purple pl-1"
            >
              Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
